import type { SupabaseClient } from "@supabase/supabase-js";

/**
 * PKCE email invite, magic link, OAuth, and password recovery often land with `?code=` on the
 * configured `redirectTo` URL. If that URL is `/` (or anything other than `/auth/callback`),
 * {@link AuthCallback} never runs — exchange here before `getSession()` so the user enters the app.
 */
export async function consumeAuthReturnInBrowser(sb: SupabaseClient): Promise<void> {
  if (typeof window === "undefined") return;
  const url = new URL(window.location.href);
  const code = url.searchParams.get("code");
  if (!code) return;

  const { error } = await sb.auth.exchangeCodeForSession(code);
  if (error) {
    const msg = String(error.message || "");
    if (!/PKCE code verifier not found/i.test(msg)) {
      console.warn("[auth] exchangeCodeForSession:", msg);
    }
    // Keep `?code=` in the address bar so we do not strand users on the welcome screen with no hint
    // that the first invite/magic link failed (stripping made it look like a fresh visit).
    return;
  }

  url.searchParams.delete("code");
  const qs = url.searchParams.toString();
  const nextPath = `${url.pathname}${qs ? `?${qs}` : ""}${url.hash}`;
  window.history.replaceState({}, "", nextPath || "/");
}
